import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import CandidateInsightModal from '../components/results/CandidateInsightModal';
import CandidateAssessmentScoreContent from '../components/results/CandidateAssessmentScoreContent';
import CandidateProctoringDetailsContent from '../components/results/CandidateProctoringDetailsContent';
import InterviewSessionModal from '../pages/results/components/InterviewSessionModal';
import { getTotalRiskPointsFromDetails } from '../components/results/proctoringUtils';

const resolveId = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value._id || value.id || null;
  }
  return null;
};

const getCandidateId = (candidate) => candidate?._id || candidate?.id || null;
const getScreeningId = (candidate) => {
  if (!candidate) return null;
  return (
    candidate.screeningId ||
    resolveId(candidate.screening?._id ? candidate.screening : candidate.screeningId) ||
    resolveId(candidate.screening)
  );
};

export default function useCandidateScoreModals() {
  const detailCacheRef = useRef(new Map());
  const [detailVersion, setDetailVersion] = useState(0);

  const ensureDetailedCandidate = useCallback(async (candidate) => {
    const candidateId = getCandidateId(candidate);
    const screeningId = getScreeningId(candidate);
    if (!candidateId || !screeningId) {
      toast.error('This candidate is missing screening context.');
      return null;
    }
    if (detailCacheRef.current.has(candidateId)) {
      return detailCacheRef.current.get(candidateId);
    }
    try {
      const res = await axios.get(`/api/screenings/${screeningId}/candidates/${candidateId}`);
      const detailed = res.data?.candidate || null;
      if (detailed) {
        detailCacheRef.current.set(candidateId, detailed);
        setDetailVersion((version) => version + 1);
      }
      return detailed;
    } catch (error) {
      // Silently swallow — caller will handle missing data gracefully in the modal
      return null;
    }
  }, []);

  const mergeWithDetail = useCallback(
    (candidate) => {
      if (!candidate) return null;
      const candidateId = getCandidateId(candidate);
      if (!candidateId) return candidate;
      const detailed = detailCacheRef.current.get(candidateId);
      return detailed ? { ...candidate, ...detailed } : candidate;
    },
    [detailVersion]
  );

  const getCandidateContext = useCallback(
    (candidate) => {
      const merged = mergeWithDetail(candidate) || candidate;
      if (!merged) return null;
      const context = {
        candidate: merged,
        candidateId: getCandidateId(merged),
        screeningId: getScreeningId(merged),
        assessmentId:
          resolveId(merged.assessmentId) ||
          resolveId(merged.assessment?.assessmentId) ||
          resolveId(merged.assessment?.id) ||
          resolveId(merged.screening?.assessmentId),
        interviewId:
          resolveId(merged.interviewId) ||
          resolveId(merged.interview?.interviewId) ||
          resolveId(merged.interview?._id) ||
          resolveId(merged.screening?.interviewId),
        interviewSessionId:
          resolveId(merged.interviewSessionId) ||
          resolveId(merged.latestInterviewSessionId) ||
          resolveId(merged.interviewSessions?.[0]),
        proctoringSessionId: resolveId(merged.proctoringSessionId),
        email: merged.email || merged.candidateEmail || merged.candidate?.email || '',
        assessmentTotalPoints: merged.assessmentTotalPoints,
      };
      return context;
    },
    [mergeWithDetail]
  );

  // Resume modal state
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [resumeModalCandidate, setResumeModalCandidate] = useState(null);
  const [resumeModalInitialTab, setResumeModalInitialTab] = useState('score');
  const [resumeModalLoading, setResumeModalLoading] = useState(false);

  const closeResumeModal = useCallback(() => {
    setResumeModalOpen(false);
    setResumeModalCandidate(null);
    setResumeModalInitialTab('score');
    setResumeModalLoading(false);
  }, []);

  const openCandidateResumeBreakdown = useCallback(
    async (candidate, initialTab = 'score') => {
      const candidateId = getCandidateId(candidate);
      if (!candidateId) {
        toast.error('Unable to open resume – candidate reference missing.');
        return;
      }
      setResumeModalCandidate(candidate);
      setResumeModalInitialTab(initialTab === 'candidate' ? 'candidate' : initialTab);
      setResumeModalOpen(true);
      if (!detailCacheRef.current.has(candidateId)) {
        setResumeModalLoading(true);
        await ensureDetailedCandidate(candidate);
        setResumeModalLoading(false);
      }
    },
    [ensureDetailedCandidate]
  );

  // Assessment modal state
  const [assessmentModalState, setAssessmentModalState] = useState({
    isOpen: false,
    candidate: null,
    activeTab: 'score',
    scoreDetails: null,
    proctoringDetails: null,
    loadingScore: false,
    loadingProctor: false,
  });

  const closeAssessmentModal = useCallback(() => {
    setAssessmentModalState({
      isOpen: false,
      candidate: null,
      activeTab: 'score',
      scoreDetails: null,
      proctoringDetails: null,
      loadingScore: false,
      loadingProctor: false,
    });
  }, []);

  const loadAssessmentScoreDetails = useCallback(
    async (candidate) => {
      await ensureDetailedCandidate(candidate);
      const context = getCandidateContext(candidate);
      if (!context?.assessmentId || !context.email) {
        setAssessmentModalState((prev) => ({
          ...prev,
          loadingScore: false,
          scoreDetails: { notAttempted: true },
        }));
        return;
      }
      try {
        const resp = await axios.get(
          `/api/assessments/${context.assessmentId}/results/${encodeURIComponent(context.email)}`
        );
        setAssessmentModalState((prev) => ({
          ...prev,
          loadingScore: false,
          scoreDetails: resp.data || null,
        }));
      } catch (error) {
        const is404 = error.response?.status === 404;
        if (!is404) toast.error('Failed to load assessment score.');
        setAssessmentModalState((prev) => ({
          ...prev,
          loadingScore: false,
          scoreDetails: is404
            ? { notAttempted: true }
            : { error: true, message: error.response?.data?.message || 'Failed to load assessment score.' },
        }));
      }
    },
    [ensureDetailedCandidate, getCandidateContext]
  );

  // Fetch full session detail (includes videoProctoringResult + categories).
  const fetchFullSession = useCallback(async (session) => {
    if (!session?.sessionId) return session;
    try {
      const detailResp = await axios.get(`/api/proctoring/session/${session.sessionId}`);
      return detailResp.data?.session || session;
    } catch (_) {
      return session;
    }
  }, []);

  // Fetch full session detail by string sessionId (the human-readable ID, not Mongo _id).
  const fetchSessionByStringId = useCallback(async (sessionIdStr, moduleTitle) => {
    if (!sessionIdStr) return null;
    try {
      const resp = await axios.get(`/api/proctoring/session/${sessionIdStr}`);
      const sess = resp.data?.session || null;
      if (sess && moduleTitle && !sess.moduleTitle) sess.moduleTitle = moduleTitle;
      return sess;
    } catch (_) {
      return null;
    }
  }, []);

  const loadAssessmentProctoringDetails = useCallback(
    async (candidate, scoreDetailsArg) => {
      await ensureDetailedCandidate(candidate);
      const context = getCandidateContext(candidate);
      if (!context?.assessmentId || !context.email) {
        setAssessmentModalState((prev) => ({
          ...prev,
          loadingProctor: false,
          proctoringDetails: { notAttempted: true },
        }));
        return;
      }
      try {
        // Always ensure we have scoreDetails — it's the source of truth for isMultiModule.
        let scoreDetails = scoreDetailsArg;
        if (!scoreDetails || scoreDetails.notAttempted || scoreDetails.error) {
          try {
            const resp = await axios.get(
              `/api/assessments/${context.assessmentId}/results/${encodeURIComponent(context.email)}`
            );
            scoreDetails = resp.data || null;
            // Cache it so switching to the score tab doesn't re-fetch.
            if (scoreDetails) {
              setAssessmentModalState((prev) => ({
                ...prev,
                scoreDetails: prev.scoreDetails || scoreDetails,
              }));
            }
          } catch (_) {
            scoreDetails = null;
          }
        }

        const isMultiModule = scoreDetails?.assessment?.isMultiModule === true;
        const moduleScores = scoreDetails?.candidate?.moduleScores || [];

        console.warn('[PROCTOR-DEBUG] isMultiModule:', isMultiModule);
        console.warn('[PROCTOR-DEBUG] moduleScores count:', moduleScores.length);
        console.warn('[PROCTOR-DEBUG] moduleScores proctoringSessionIds:', moduleScores.map(ms => ms.proctoringSessionId));
        window.__PROCTOR_DEBUG = { isMultiModule, moduleScores: moduleScores.map(ms => ({ title: ms.moduleTitle, psid: ms.proctoringSessionId })) };

        // --- Multi-module path: load one session per module ---
        if (isMultiModule && moduleScores.length > 0) {
          // Primary strategy: use the proctoringSessionId stored on each moduleScore
          // (set during submit-module). This is the most reliable link.
          const hasLinkedSessions = moduleScores.some((ms) => !!ms.proctoringSessionId);
          console.log('[PROCTOR-DEBUG] hasLinkedSessions:', hasLinkedSessions);

          if (hasLinkedSessions) {
            const moduleSessions = await Promise.all(
              moduleScores.map((ms) =>
                fetchSessionByStringId(ms.proctoringSessionId, ms.moduleTitle)
              )
            );
            // Filter out nulls (incomplete modules) but keep at least one.
            const validSessions = moduleSessions.filter(Boolean);
            console.log('[PROCTOR-DEBUG] validSessions from linked:', validSessions.length, validSessions.map(s => s?.sessionId));
            if (validSessions.length > 0) {
              setAssessmentModalState((prev) => ({
                ...prev,
                loadingProctor: false,
                proctoringDetails: validSessions,
              }));
              return;
            }
          }

          // Fallback: search all sessions by email+assessment, group by moduleIndex.
          const sessionsResp = await axios.get('/api/proctoring/sessions');
          const allSessions = sessionsResp.data?.sessions || [];
          const relevant = allSessions.filter((s) => {
            const matchesEmail = (s.candidateEmail || '').toLowerCase() === context.email.toLowerCase();
            const sAssessmentId = String(resolveId(s.assessmentId) || resolveId(s.assessmentId?._id) || '');
            return matchesEmail && sAssessmentId === String(context.assessmentId);
          });
          console.log('[PROCTOR-DEBUG] relevant sessions from broad search:', relevant.length, relevant.map(s => ({ sessionId: s.sessionId, moduleIndex: s.moduleIndex, moduleTitle: s.moduleTitle })));

          const withModuleIndex = relevant.filter((s) => s.moduleIndex != null);
          if (withModuleIndex.length > 0) {
            const moduleSessions = await Promise.all(
              withModuleIndex
                .slice()
                .sort((a, b) => (a.moduleIndex ?? 0) - (b.moduleIndex ?? 0))
                .map((s) => fetchFullSession(s))
            );
            setAssessmentModalState((prev) => ({
              ...prev,
              loadingProctor: false,
              proctoringDetails: moduleSessions,
            }));
            return;
          }

          // Fallback: multiple sessions without moduleIndex — assign to modules by creation order.
          // This handles old clients that didn't tag sessions with moduleIndex.
          if (relevant.length > 1) {
            const sorted = relevant.slice().sort(
              (a, b) => new Date(a.startedAt || a.createdAt) - new Date(b.startedAt || b.createdAt)
            );
            const moduleSessions = await Promise.all(
              sorted.map(async (s, i) => {
                const full = await fetchFullSession(s);
                if (full && !full.moduleTitle && moduleScores[i]?.moduleTitle) {
                  full.moduleTitle = moduleScores[i].moduleTitle;
                }
                return full;
              })
            );
            const valid = moduleSessions.filter(Boolean);
            if (valid.length > 1) {
              setAssessmentModalState((prev) => ({
                ...prev,
                loadingProctor: false,
                proctoringDetails: valid,
              }));
              return;
            }
          }

          // Only 1 session found for multi-module: show as single view (legacy).
          if (relevant.length > 0) {
            const best =
              relevant.find((s) => s.status === 'completed') ||
              relevant.find((s) => (s.violations?.totalCount || 0) > 0) ||
              relevant[0];
            const fullSession = await fetchFullSession(best);
            setAssessmentModalState((prev) => ({
              ...prev,
              loadingProctor: false,
              proctoringDetails: fullSession,
            }));
            return;
          }

          setAssessmentModalState((prev) => ({
            ...prev,
            loadingProctor: false,
            proctoringDetails: { notAttempted: true },
          }));
          return;
        }

        // --- Single-module path ---
        let session = null;
        if (context.proctoringSessionId) {
          try {
            const exact = await axios.get('/api/proctoring/sessions', {
              params: { sessionId: context.proctoringSessionId },
            });
            session = (exact.data?.sessions || []).find(
              (s) => (s._id || s.sessionId)?.toString() === context.proctoringSessionId
            );
          } catch (_) {
            // fall through
          }
        }
        if (!session) {
          const sessionsResp = await axios.get('/api/proctoring/sessions');
          const allSessions = sessionsResp.data?.sessions || [];
          const relevant = allSessions.filter((s) => {
            const matchesEmail = (s.candidateEmail || '').toLowerCase() === context.email.toLowerCase();
            const sAssessmentId = String(resolveId(s.assessmentId) || resolveId(s.assessmentId?._id) || '');
            return matchesEmail && sAssessmentId === String(context.assessmentId);
          });
          if (relevant.length) {
            const withNumericRisk = relevant.filter(
              (s) => typeof getTotalRiskPointsFromDetails(s) === 'number'
            );
            if (withNumericRisk.length) {
              session = withNumericRisk.reduce((max, s) =>
                getTotalRiskPointsFromDetails(s) > getTotalRiskPointsFromDetails(max) ? s : max,
                withNumericRisk[0]
              );
            }
            if (!session) {
              session =
                relevant.find((s) => s.status === 'completed') ||
                relevant.find((s) => (s.violations?.totalCount || 0) > 0) ||
                relevant[0];
            }
          }
        }
        if (session) {
          const fullSession = await fetchFullSession(session);
          setAssessmentModalState((prev) => ({
            ...prev,
            loadingProctor: false,
            proctoringDetails: fullSession,
          }));
        } else {
          setAssessmentModalState((prev) => ({
            ...prev,
            loadingProctor: false,
            proctoringDetails: { notAttempted: true },
          }));
        }
      } catch (error) {
        const is404 = error.response?.status === 404;
        if (!is404) toast.error('Failed to load proctoring details.');
        setAssessmentModalState((prev) => ({
          ...prev,
          loadingProctor: false,
          proctoringDetails: is404
            ? { notAttempted: true }
            : { error: true, message: error.response?.data?.message || 'Failed to load proctoring details.' },
        }));
      }
    },
    [ensureDetailedCandidate, getCandidateContext, fetchFullSession, fetchSessionByStringId]
  );

  const openAssessmentScoreModal = useCallback(
    (candidate) => {
      if (!getCandidateId(candidate)) {
        toast.error('Unable to open assessment results for this candidate.');
        return;
      }
      setAssessmentModalState({
        isOpen: true,
        candidate,
        activeTab: 'score',
        scoreDetails: null,
        proctoringDetails: null,
        loadingScore: true,
        loadingProctor: false,
      });
      loadAssessmentScoreDetails(candidate);
    },
    [loadAssessmentScoreDetails]
  );

  const openAssessmentProctoringModal = useCallback(
    (candidate) => {
      if (!getCandidateId(candidate)) {
        toast.error('Unable to open proctoring summary for this candidate.');
        return;
      }
      setAssessmentModalState({
        isOpen: true,
        candidate,
        activeTab: 'proctoring',
        scoreDetails: null,
        proctoringDetails: null,
        loadingScore: false,
        loadingProctor: true,
      });
      loadAssessmentProctoringDetails(candidate, null);
    },
    [loadAssessmentProctoringDetails]
  );

  const setAssessmentTab = useCallback(
    (tab) => {
      setAssessmentModalState((prev) => {
        const next = { ...prev, activeTab: tab };
        if (tab === 'score' && prev.candidate && !prev.scoreDetails && !prev.loadingScore) {
          loadAssessmentScoreDetails(prev.candidate);
          return { ...next, loadingScore: true };
        }
        if (tab === 'proctoring' && prev.candidate && !prev.proctoringDetails && !prev.loadingProctor) {
          loadAssessmentProctoringDetails(prev.candidate, prev.scoreDetails);
          return { ...next, loadingProctor: true };
        }
        return next;
      });
    },
    [loadAssessmentProctoringDetails, loadAssessmentScoreDetails]
  );

  // Interview modal state
  const [interviewModalState, setInterviewModalState] = useState({
    isOpen: false,
    candidate: null,
    session: null,
    interviewDetails: null,
    loading: false,
    error: '',
    initialTab: 'analysis',
  });

  const closeInterviewModal = useCallback(() => {
    setInterviewModalState({
      isOpen: false,
      candidate: null,
      session: null,
      interviewDetails: null,
      loading: false,
      error: '',
      initialTab: 'analysis',
    });
  }, []);

  const openInterviewSessionForCandidate = useCallback(
    async (candidate, initialTab = 'analysis') => {
      const candidateId = getCandidateId(candidate);
      if (!candidateId) {
        toast.error('Unable to open interview replay for this candidate.');
        return;
      }
      setInterviewModalState({
        isOpen: true,
        candidate,
        session: null,
        interviewDetails: null,
        loading: true,
        error: '',
        initialTab,
      });
      await ensureDetailedCandidate(candidate);
      const context = getCandidateContext(candidate);
      if (!context?.interviewSessionId) {
        setInterviewModalState((prev) => ({
          ...prev,
          loading: false,
          error: 'Candidate has not attempted the interview yet.',
          notAttempted: true,
        }));
        return;
      }
      try {
        const sessionResp = await axios.get(`/api/ai-interviews/session/${context.interviewSessionId}/replay`);
        const session = sessionResp.data?.session || null;
        if (!session) {
          throw new Error('Session missing');
        }
        let interviewDetails = null;
        if (context.interviewId) {
          try {
            const interviewResp = await axios.get(`/api/ai-interviews/${context.interviewId}`);
            interviewDetails = interviewResp.data?.interview || null;
          } catch (error) {
            // Non-blocking
          }
        }
        setInterviewModalState((prev) => ({
          ...prev,
          session,
          interviewDetails,
          loading: false,
          error: '',
        }));
      } catch (error) {
        const is404 = error.response?.status === 404;
        const msg = is404
          ? 'Candidate has not attempted the interview yet.'
          : 'Failed to load interview session.';
        if (!is404) toast.error(error.response?.data?.message || msg);
        setInterviewModalState((prev) => ({
          ...prev,
          loading: false,
          error: msg,
          notAttempted: is404,
        }));
      }
    },
    [ensureDetailedCandidate, getCandidateContext]
  );

  const resumeCandidateForModal = useMemo(
    () => mergeWithDetail(resumeModalCandidate),
    [mergeWithDetail, resumeModalCandidate]
  );

  const assessmentCandidateForModal = useMemo(
    () => mergeWithDetail(assessmentModalState.candidate),
    [assessmentModalState.candidate, mergeWithDetail]
  );

  const ScoreModals = () => {
    if (typeof document === 'undefined') return null;
    return createPortal(
      <>
        {resumeModalOpen && (
          <CandidateInsightModal
            isOpen={resumeModalOpen}
            onClose={closeResumeModal}
            candidate={resumeCandidateForModal}
            initialTab={resumeModalInitialTab}
            candidateDetailsOnly={resumeModalInitialTab === 'candidate'}
            detailsLoading={resumeModalLoading}
          />
        )}

        {assessmentModalState.isOpen && assessmentCandidateForModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] overflow-y-auto h-full w-full z-[110]">
            <div className="relative top-10 mx-auto border w-11/12 sm:w-full sm:max-w-5xl shadow-xl rounded-xl bg-white max-h-[90vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h3 className="text-2xl font-bold text-gray-900">Candidate Results</h3>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => setAssessmentTab('score')}
                      className={`px-3 py-1.5 text-sm font-semibold rounded-md border ${assessmentModalState.activeTab === 'score'
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                        : 'text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      Assessment Score
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssessmentTab('proctoring')}
                      className={`px-3 py-1.5 text-sm font-semibold rounded-md border ${assessmentModalState.activeTab === 'proctoring'
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                        : 'text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      Proctoring Details
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeAssessmentModal}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="px-6 py-4 overflow-y-auto flex-1">
                {assessmentModalState.activeTab === 'score' ? (
                  assessmentModalState.loadingScore ? (
                    <div className="py-10 text-center text-sm text-gray-500">Loading assessment score…</div>
                  ) : (
                    <CandidateAssessmentScoreContent details={assessmentModalState.scoreDetails} />
                  )
                ) : assessmentModalState.activeTab === 'proctoring' ? (
                  assessmentModalState.loadingProctor ? (
                    <div className="py-10 text-center text-sm text-gray-500">Loading proctoring details…</div>
                  ) : (
                    <CandidateProctoringDetailsContent
                      session={assessmentModalState.proctoringDetails}
                      candidate={assessmentCandidateForModal}
                    />
                  )
                ) : null}
              </div>
            </div>
          </div>
        )}

        {interviewModalState.isOpen && (
          interviewModalState.session ? (
            <InterviewSessionModal
              session={interviewModalState.session}
              interviewDetails={interviewModalState.interviewDetails}
              onClose={closeInterviewModal}
              initialTab={interviewModalState.initialTab}
            />
          ) : (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center z-[110]">
              <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md text-center space-y-4">
                {interviewModalState.notAttempted ? (
                  <>
                    <div className="text-4xl">🎙️</div>
                    <p className="text-base font-semibold text-gray-600">Candidate has not attempted the interview yet.</p>
                  </>
                ) : (
                  <p className="text-sm text-gray-700">
                    {interviewModalState.error || 'Loading interview session…'}
                  </p>
                )}
                <div className="flex items-center justify-center gap-3">
                  {!interviewModalState.error && !interviewModalState.notAttempted && (
                    <span className="h-4 w-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                  )}
                  <button
                    type="button"
                    onClick={closeInterviewModal}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )
        )}
      </>,
      document.body
    );
  };

  return {
    openCandidateResumeBreakdown,
    handleInlineViewAssessmentScore: openAssessmentScoreModal,
    handleInlineViewAssessmentProctoring: openAssessmentProctoringModal,
    openInterviewSessionForCandidate,
    ScoreModals,
  };
}
